import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {combineLatest, Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import moment from 'moment';

import {environment} from '@env/environment';
import {IJob, JOB_TYPE, PROCESS_NAME, ProcessFriendlyNamesMap} from '@app/core/model/job.model';
import {
    DataAccessExecutionDetail,
    DataAccessExecutionStatus,
    DataAccessProgress,
    DataAccessStepName,
    DataAccessStepNameMap
} from '@app/views/dashboard/dashboard.model';
import {ProcessHistoryState} from '@app/views/admin/components/sync-history-timeline/models/process-history-state.model';
import {OrgsService} from '@app/core/service/orgs.service';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';


@Injectable({
    providedIn: 'root'
})
export class JobManagementService {

    constructor(
        private http: HttpClient,
        private orgsService: OrgsService,
        private snackBar: MatSnackBar
    ) {
    }

    getAllJobs(): Observable<IJob[]> {
        return combineLatest([
            this.getAllAccessJobs().pipe(catchError((error) => this.showError(error))),
            this.getAllSyncJobs().pipe(catchError((error) => this.showError(error))),
            this.getAllEventJobs().pipe(catchError((error) => this.showError(error)))
        ]).pipe(
            map(([accessJobs = [], syncJobs = [], eventLJobs = []]: [IJob[], IJob[],  IJob[]]) => [...accessJobs, ...syncJobs, ...eventLJobs])
        );
    }

    getAllRunningProcesses(): Observable<IJob[]> {
        return combineLatest([
            this.getDataAccessRunningProcesses().pipe(catchError((error) => this.showError(error))),
            this.getDataSyncRunningProcesses().pipe(catchError((error) => this.showError(error))),
            this.getDeleteOrganizationProgress().pipe(catchError((error) => this.showError(error)))
        ]).pipe(
            map(([accessJobs = [], syncJobs = [], deleteOrgsJobs = []]: [IJob[], IJob[], IJob[]]) => [...accessJobs, ...syncJobs, ...deleteOrgsJobs])
        );
    }

    getAllProcessesHistory(days: number = 1): Observable<IJob[]> {
        return combineLatest([
            this.getDataAccessHistory(days).pipe(catchError((error) => this.showError(error))),
            this.getProcessesHistory(days).pipe(catchError((error) => this.showError(error)))
        ]).pipe(
            map(([accessJobs = [], processJobs = []]: [IJob[], IJob[]]) => {
                accessJobs.forEach((job: IJob) =>
                    job.details = this.getDataAccessStepFriendlyName(<DataAccessStepName>job.processName, false, true)
                );
                processJobs.forEach((job: IJob) =>
                    job.details = this.getProcessFriendlyName(<PROCESS_NAME>job.processName)
                );
                return [...accessJobs, ...processJobs];
            })
        );
    }

    getDataSyncRunningProcesses(): Observable<IJob[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/runningProcesses`;

        return this.http.get<IJob[]>(url)
            .pipe(
                map((jobs: IJob[]) => jobs.map((job: IJob) => {
                    job.expectedRemainingTime = this.getDataSyncJobExpectedRemainingTime(job);
                    job.progress = this.getDataSyncJobProgress(job);
                    job.startExecution = this.getDate(job.startExecution);
                    job.duration = this.getDuration(job.startExecution, new Date());
                    return job;
                })),
                catchError(this.handleError)
            );
    }

    getDataAccessRunningProcesses(): Observable<IJob[]> {
        const url = `${environment.datahubBaseURI}/api/v2/rosterAccess/access-in-progress`;
        return this.http
            .get<DataAccessProgress[]>(url)
            .pipe(
                map((processes: DataAccessProgress[]) =>
                    processes.map(process => this.mapDataAccessProgressBarToJobInterface(process))
                ),
                catchError(this.handleError)
            );
    }

    getDeleteOrganizationProgress(): Observable<IJob[]> {
        return this.orgsService.getDeleteOrganizationProgress()
            .pipe(
                map((progressBars: {[key: string]: number}) =>
                    this.mapOrgDeleteProgressBarsToJobInterface(progressBars)
                ),
                catchError(this.handleError)
            );
    }


    getAllEventJobs(): Observable<IJob[]> {
        const url = `${environment.datahubBaseURI}/api/v2/job-queues/event`;

        return this.http.get<IJob[]>(url)
            .pipe(
                map((jobs: IJob[]) => jobs.map((job: IJob) => {
                    job.creationTs = this.getDate(job.creationTs);
                    job.duration = this.getDuration(job.creationTs, new Date());
                    return job;
                })),
                catchError(this.handleError)
            );
    }

    getAllAccessJobs(): Observable<IJob[]> {
        const url = `${environment.datahubBaseURI}/api/v2/job-queues/access`;

        return this.http.get<IJob[]>(url)
            .pipe(
                map((jobs: IJob[]) => jobs.map((job: IJob) => {
                    job.creationTs = this.getDate(job.creationTs);
                    job.duration = this.getDuration(job.creationTs, new Date());
                    return job;
                })),
                catchError(this.handleError)
            );
    }

    getAllSyncJobs(): Observable<IJob[]> {
        const url = `${environment.datahubBaseURI}/api/v2/job-queues/sync`;

        return this.http.get<IJob[]>(url)
            .pipe(
                map((jobs: IJob[]) => jobs.map((job: IJob) => {
                    job.creationTs = this.getDate(job.creationTs);
                    job.duration = this.getDuration(job.creationTs, new Date());
                    return job;
                })),
                catchError(this.handleError)
            );
    }

    getJobsByDistrictGuid(districtGuid: string): Observable<IJob[]> {
        const url = `${environment.datahubBaseURI}/api/v2/job-queues/district/${districtGuid}`;

        return this.http.get<IJob[]>(url)
            .pipe(catchError(this.handleError));
    }

    getJobsByApplicationGuid(applicationGuid: string): Observable<IJob[]> {
        const url = `${environment.datahubBaseURI}/api/v2/job-queues/vendor/${applicationGuid}`;

        return this.http.get<IJob[]>(url)
            .pipe(catchError(this.handleError));
    }

    getJobsByAppAndDistrictGuid(applicationGuid: string, districtGuid: string): Observable<IJob[]> {
        const url = `${environment.datahubBaseURI}/api/v2/job-queues/vendor/${applicationGuid}/${districtGuid}`;

        return this.http.get<IJob[]>(url)
            .pipe(catchError(this.handleError));
    }

    updateJobPriority(job: any, priority: number): Observable<IJob> {
        const url = `${environment.datahubBaseURI}/api/v2/job-queues?priority=${priority}`;

        return this.http.put<IJob>(url, job)
            .pipe(catchError(this.handleError));
    }

    deleteJob(processId: number) {
        const url = `${environment.datahubBaseURI}/api/v2/sync-job/${processId}`;

        return this.http.delete(url)
            .pipe(catchError(this.handleError));
    }

    getProcessesHistory(days: number = 1): Observable<IJob[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/lastProcesses/${days}`;

        return this.http.get<IJob[]>(url)
            .pipe(
                map((processesHistory: IJob[]) =>
                    processesHistory.map((process: IJob) => {
                        process.startExecution = this.getDate(process.startExecution);
                        process.endExecution = this.getDate(process.endExecution);
                        process.duration = this.getDuration(process.startExecution, process.endExecution || new Date());
                        return process;
                    })
                ),
                catchError(this.handleError)
            );
    }

    getDataAccessHistory(days: number = 1): Observable<IJob[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/lastAccessHistory/${days}`;

        return this.http.get<DataAccessExecutionDetail[]>(url)
            .pipe(
                map((dataAccessExecutionDetails: DataAccessExecutionDetail[]) =>
                    dataAccessExecutionDetails.map((detail: DataAccessExecutionDetail) =>
                        this.mapDataAccessExecutionDetailToJobInterface(detail)
                    )
                ),
                catchError(this.handleError)
            );
    }

    getProcessFriendlyName(value: PROCESS_NAME, isTooltip: boolean = false): string {
        if (ProcessFriendlyNamesMap.has(value)) {
            return ProcessFriendlyNamesMap.get(value)[isTooltip ? 'tooltip' : 'friendlyName'];
        } else {
            return value ? value.replace(/[_-]/g, ' ') : '';
        }
    }

    getDataAccessStepFriendlyName(value: DataAccessStepName, isTooltip: boolean = false, isJobDetail: boolean = false): string {
        if (DataAccessStepNameMap.has(value)) {
            if (isTooltip) {
                return DataAccessStepNameMap.get(value).tooltip;
            } else if (isJobDetail) {
                return DataAccessStepNameMap.get(value).jobDetail;
            } else {
                return DataAccessStepNameMap.get(value).friendlyName;
            }
        } else {
            return value ? value.replace(/[_-]/g, ' ') : '';
        }
    }

    private mapDataAccessProgressBarToJobInterface(progressBar: DataAccessProgress): IJob {
        return <IJob>{
            id: null,
            processId: null,
            processName: progressBar.stepName,
            nodeId: null,
            eventBody: null,
            eventClass: null,
            weight: null,
            creationTs: null,
            startExecution: progressBar.started,
            endExecution: null,
            applicationGuid: progressBar.applicationId,
            integrationId: progressBar.integrationId,
            defaultApplicationGuid: progressBar.integrationId,
            districtGuid: progressBar.orgId,
            priority: null,
            jobType: JOB_TYPE.RosterDataAccessJob,
            state: ProcessHistoryState.RUNNING,
            sessionId: null,
            statId: null,
            progress: progressBar.progress,
            expectedRemainingTime: this.getDataAccessJobExpectedRemainingTime(progressBar)
        };
    }

    private mapDataAccessExecutionDetailToJobInterface(dataAccessExecutionDetail: DataAccessExecutionDetail): IJob {
        const state = (dataAccessExecutionDetail.status === DataAccessExecutionStatus.START)
            ? ProcessHistoryState.RUNNING
            : ProcessHistoryState.OK;
        const startExecution = this.getDate(dataAccessExecutionDetail.started);
        const endExecution = this.getDate(dataAccessExecutionDetail.finished);
        const duration = this.getDuration(startExecution, endExecution || new Date());

        return <IJob>{
            id: null,
            processId: null,
            processName: dataAccessExecutionDetail.stepName,
            nodeId: null,
            eventBody: null,
            eventClass: null,
            weight: null,
            creationTs: null,
            startExecution: startExecution,
            endExecution: endExecution,
            duration: duration,
            applicationGuid: dataAccessExecutionDetail.applicationGuid,
            integrationId: dataAccessExecutionDetail.integrationId,
            defaultApplicationGuid: dataAccessExecutionDetail.integrationId,
            districtGuid: dataAccessExecutionDetail.orgId,
            priority: null,
            jobType: JOB_TYPE.RosterDataAccessJob,
            state: state,
            sessionId: null,
            statId: null
        };
    }

    private mapOrgDeleteProgressBarsToJobInterface(progressBars: {[key: string]: number}): IJob[] {
        return Object.keys(progressBars).map((key: string) => <IJob>{
            districtGuid: key,
            progress: progressBars[key],
            jobType: JOB_TYPE.EventJob,
            state: ProcessHistoryState.RUNNING,
        });
    }

    private getDataAccessJobExpectedRemainingTime(progressBar: DataAccessProgress): number {
        if (progressBar.started && progressBar.progress > 0 && progressBar.progress < 100) {
            const startDate = moment(progressBar.started);
            const now = moment(new Date());
            const secondsPassed = now.diff(startDate, 'seconds');

            return startDate.add(secondsPassed * 100 / progressBar.progress, 'seconds').diff(now, 'seconds');
        }
        return null;
    }

    private getDataSyncJobExpectedRemainingTime(job: IJob): number {
        if (job.startExecution && !job.endExecution && job.averageDurationExecutionInMinutes > 0) {
            const startDate = moment(job.startExecution);
            const now = moment(new Date());
            const expectedEndTime = startDate.add(job.averageDurationExecutionInMinutes, 'minutes');

            return expectedEndTime.isAfter() ? expectedEndTime.diff(now, 'seconds') : null;
        }
        return null;
    }

    private getDataSyncJobProgress(job: IJob): number {
        if (job.averageDurationExecutionInMinutes) {
            const startDate = moment(job.startExecution);
            const now = moment(new Date());
            const secondsPassed = now.diff(startDate, 'seconds');

            return Math.min(Math.round((secondsPassed / (job.averageDurationExecutionInMinutes * 60)) * 100), 100);
        }

        return 100;
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned ${error.status}, ${error.error}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }

    private getDate(date: any): Date {
        return (typeof date === 'string' ? new Date(date) : date);
    }

    private getDuration(startDate: Date, endDate: Date): number {
        return (startDate && endDate)
            ? Math.abs(Math.ceil((endDate.getTime() - startDate.getTime()) / 1000))
            : null;
    }

    private showError(msg: string, defaultResult: any = []): Observable<any> {
        this.snackBar.open(msg, '', <MatSnackBarConfig>{duration: 3000});
        return of(defaultResult);
    }
}
